import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { WebShopService } from '../../services/webShop.service';
import { MAX_BASKETITEM_COUNT, WebshopSettingsDataModel2 } from '../../shared/models';
import Utils from '../../shared/utils';
import { InternationalisationService } from '../../forked/angular-internationalisation';
import { IRemoteGroupBasketItem, IRemoteGroupBasketItemExtra, IRemoteGroupMember, IRemoteGroupMemberItems, IRemoteOrdeGroupOverviewService } from './remoteOrderGroupOverview.facade';
import { RemoteGroupOrderService } from '../../services/RemoteGroupOrder.service';
import { Article, GroupBasketItemUiData, GroupMemberUiData, GroupUiData, MemberStatus } from '../../services/IRemoteGroupOrderService';
import { EventmanagerService } from '../../services/eventmanager.service';
import { take } from "rxjs/operators";

class RemoteGroupMember implements IRemoteGroupMember {
  public basketItems_count: number;
  public basketItems_amount: number;

  constructor(private memberData: GroupMemberUiData, groupData: GroupUiData) {
    const userId = memberData.userId;
    this.basketItems_count = 0;
    this.basketItems_amount = 0;

    groupData.basket_items.forEach((item) => {
      if (item.userId !== userId) return;
      this.basketItems_count += item.count;
      this.basketItems_amount += item.count * item.price;
    });

    this.basketItems_amount = Utils.centsToCurrency(this.basketItems_amount);
  }

  public get isGroupCreator(): boolean {
    return this.memberData.isGroupCreator;
  }

  public get groupId(): number {
    return this.memberData.groupId;
  }

  public get userId(): number {
    return this.memberData.userId;
  }

  public get name(): string {
    return this.memberData.name;
  }

  public get itemsCount(): number {
    return this.basketItems_count;
  }

  public get itemsAmount(): number {
    return this.basketItems_amount;
  }

  public get stateReady(): boolean {
    return this.memberData.stateReady === MemberStatus.Ready;
  }

}

class RemoteGroupBasketItemExtra implements IRemoteGroupBasketItemExtra {
  constructor(private _model: IRemoteGroupBasketItemExtra) { }

  public get title_de(): string {
    return this._model.title_de;
  }
  public get title_en(): string {
    return this._model.title_en;
  }

  public get title(): string {
    return InternationalisationService.locale === "de" || Utils.IsNullOrWhiteSpace(this._model.title_en)
      ? this._model.title_de
      : this._model.title_en;
  }

  public get id(): number {
    return this._model.id;
  }
  public set id(v: number) {
    throw new Error("Setter not implemented.");
  }

  public get subIndex(): number {
    return 0;
  }

  public get count(): number {
    return this._model.count;
  }
}

class RemoteGroupBasketItem implements IRemoteGroupBasketItem {
  public extraItems: RemoteGroupBasketItemExtra[];

  constructor(
    private _service: RemoteOrderGroupOverviewService,
    private _item: GroupBasketItemUiData,
    private _isOwner: boolean,
  ) {
    this.extraItems = this._item.extras.map((extra) => new RemoteGroupBasketItemExtra(extra));
  }

  public get id(): number {
    return this._item.id;
  }

  public get label(): string {
    return InternationalisationService.locale === "de" || Utils.IsNullOrWhiteSpace(this._item.name_en)
      ? this._item.name_de
      : this._item.name_en;
  }

  public get valid_date(): number {
    return null; // TODO es braucht einen wert der mitteilt das es kein valid_date gibt this._item.valid_date;
  }

  public get deposit(): number {
    return this._item.deposit;
  }

  public get extras(): IRemoteGroupBasketItemExtra[] {
    return this.extraItems;
  }

  public get count(): number {
    return this._item.count;
  }

  public get price(): number {
    return Utils.centsToCurrency(this._item.price * this._item.count);
  }

  public get priceWithoutExtras(): number {
    return this._item.priceWithoutExtras;
  }

  public get comment(): string {
    return this._item.comment;
  }

  public async setcomment(commentValue: string) {
    this._item.comment = commentValue;
    await this._service.addComment(this._item);
  }

  public get canComment(): boolean {
    return this._isOwner && this._service.canComment;
  }

  public get canIncrement(): boolean {
    return this._isOwner && this._item.count < MAX_BASKETITEM_COUNT;
  }

  public get canDecrement(): boolean {
    return this._isOwner;
  }

  public increment() {
    if (this.canIncrement) {
      this._service.addCountItem(this._item.article);
    }
  }

  public decrement() {
    if (this.canDecrement) {
      this._service.removeCountItem(this._item.article);
    }
  }
}

class RemoteGroupMemberItems implements IRemoteGroupMemberItems {
  public items: IRemoteGroupBasketItem[];

  constructor(
    private _memberData: GroupMemberUiData,
    private _service: RemoteOrderGroupOverviewService,
    private _groupData: GroupUiData,
    private _ownerUserId: number
  ) {
    const userId = _memberData.userId;
    const isOwner = userId === this._ownerUserId;
    const userBasketItems = this._groupData.basket_items.filter((item) => item.userId === userId);
    this.items = userBasketItems.map((item) => new RemoteGroupBasketItem(_service, item, isOwner));
  }

  public get groupId(): number {
    return this._memberData.groupId;
  }

  public get userId(): number {
    return this._memberData.userId;
  }

  public get name(): string {
    return this._memberData.name;
  }

  public get stateReady(): boolean {
    return this._memberData.stateReady === MemberStatus.Ready;
  }
}

@Injectable()
export class RemoteOrderGroupOverviewService implements IRemoteOrdeGroupOverviewService, OnDestroy {
  private _webshopSettingsChangedSub: Subscription;
  private _membersChangedSubscription: Subscription;
  private _memberStateChangedSub: Subscription;
  private _itemsChangedSubscription: Subscription;
  private _groupStatusChangedSubscription: Subscription;

  private _webshopSettings: WebshopSettingsDataModel2;

  public canComment: boolean = false;

  private _groupMember: RemoteGroupMember[] = [];
  private _groupMemberSubject: BehaviorSubject<IRemoteGroupMember[]> = new BehaviorSubject<IRemoteGroupMember[]>([]);
  public get groupMembers$(): Observable<IRemoteGroupMember[]> {
    return this._groupMemberSubject.asObservable();
  }

  private _groupMemberItems: RemoteGroupMemberItems[] = [];
  private _groupMemberItemsGroupsSubject: BehaviorSubject<IRemoteGroupMemberItems[]> = new BehaviorSubject<IRemoteGroupMemberItems[]>([]);
  public get groupMemberItems$(): Observable<IRemoteGroupMemberItems[]> {
    return this._groupMemberItemsGroupsSubject.asObservable();
  }

  private _openPriceSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public get open_price$(): Observable<number> {
    return this._openPriceSubject.asObservable();
  }

  private _itemCountSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public get item_count$(): Observable<number> {
    return this._itemCountSubject.asObservable();
  }

  public get groupName() {
    return this._remoteGroupOrderService.groupName;
  }

  public get groupCreator() {
    return this._remoteGroupOrderService.creatorName;
  }

  private _groupFullSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public get groupFull$(): Observable<boolean> {
    return this._groupFullSubject.asObservable();
  }

  public get allMembersAreReady$(): Observable<boolean> {
    return this._remoteGroupOrderService.allMembersAreReady$;
  }

  public get paymentStatusFinishing$(): Observable<boolean> {
    return this._remoteGroupOrderService.groupStatusPaymentStarted$;
  }

  constructor(
    private _webShopService: WebShopService,
    private _remoteGroupOrderService: RemoteGroupOrderService,
    private _eventManagerService: EventmanagerService
  ) {
    const me = this;

    me.initial().then(() => { }); /* kein abwarten für Init notwendig */

    me._webshopSettingsChangedSub = me._webShopService.webshopSettingsDataModelObserveChanges.subscribe(async (_) => {
      const webshopSetting = await me._webShopService.getWebshopSettingsDataModel();
      me.rebindWebshopSetting(webshopSetting);
    });
  }

  ngOnDestroy(): void {
    if (this._webshopSettingsChangedSub)
      this._webshopSettingsChangedSub.unsubscribe();
    if (this._membersChangedSubscription)
      this._membersChangedSubscription.unsubscribe();
    if (this._memberStateChangedSub)
      this._memberStateChangedSub.unsubscribe();
    if (this._itemsChangedSubscription)
      this._itemsChangedSubscription.unsubscribe();
    if (this._groupStatusChangedSubscription)
      this._groupStatusChangedSubscription.unsubscribe();
  }

  private async initial(): Promise<void> {
    const me = this;
    const webshopSetting = await me._webShopService.getWebshopSettingsDataModel();
    me.rebindWebshopSetting(webshopSetting);
    await me.setParticipantsPerGroup();
    me._membersChangedSubscription = me._remoteGroupOrderService.membersChanged$.subscribe(async () => {
      await me.reloadGroupData();
    });
    me._memberStateChangedSub = me._remoteGroupOrderService.memberStateChanged$.subscribe(async () => {
      await me.reloadGroupData();
    });

    me._itemsChangedSubscription = me._remoteGroupOrderService.itemsChanged$.subscribe(async () => {
      await me.reloadGroupData();
    });

    me._groupStatusChangedSubscription = me._remoteGroupOrderService.groupStatusChanged$.subscribe(async () => {
      await me.reloadGroupData();
    });
    await me.reloadGroupData();
  }

  /** render all group Information */
  private async reloadGroupData(): Promise<void> {
    const me = this;
    //! TODO es darf nicht von der UI aufgefordert werden, die Gurppen information zu laden, das macht der Service autonom
    const groupData = await me._remoteGroupOrderService.getGroupData();
    await me.validateEventAndOpenDialog();
    await me.loadGroupData(groupData);
  }


  private rebindWebshopSetting(webshopSetting: WebshopSettingsDataModel2) {
    const me = this;
    me._webshopSettings = webshopSetting;
    me.canComment = me._webshopSettings.use_item_annotation;
  }

  /** max participants */
  private participants_per_group: number = 0;

  private async setParticipantsPerGroup() {
    const me = this;
    const groupId = me._remoteGroupOrderService.groupId;
    const memberlimit = await this._remoteGroupOrderService.retrieveGroupInformation(groupId).then(info => info.MemberLimit);
    if (memberlimit > 0) {
      me.participants_per_group = memberlimit;
    }
  }

  /** Loads group data and updates relevant observables*/
  private async loadGroupData(groupData: GroupUiData): Promise<void> {
    const me = this;
    const currentUserId = me._remoteGroupOrderService.groupManageData.userId; // TODO: actual service user token
    // Initialize group members and move current user to the front
    me._groupMember = groupData.members.map(member => new RemoteGroupMember(member, groupData));
    me._moveCurrentUserToFront(me._groupMember, currentUserId);
    me._groupMemberSubject.next(me._groupMember);

    // Get unique user ids with group basket items
    const userIdsWithGroupBasketItems = new Set(groupData.basket_items.map(item => item.userId));
    const membersWithBasketItems = groupData.members.filter(member => userIdsWithGroupBasketItems.has(member.userId));
    me._groupMemberItems = membersWithBasketItems.map(member => new RemoteGroupMemberItems(member, me, groupData, currentUserId));
    me._moveCurrentUserToFront(me._groupMemberItems, currentUserId);
    me._groupMemberItemsGroupsSubject.next(me._groupMemberItems);
    // Calculate total count and amount of group basket items
    let groupBasketItemsCount = 0;
    let groupBasketItemsAmount = 0;
    groupData.basket_items.forEach(item => {
      groupBasketItemsCount += item.count;
      groupBasketItemsAmount += item.count * item.price;
    });
    // Update observables with the calculated values
    me._itemCountSubject.next(groupBasketItemsCount);
    me._openPriceSubject.next(Utils.centsToCurrency(groupBasketItemsAmount));
    me._groupFullSubject.next(me.participants_per_group > groupData.members.length);
  }

  // Helper function to move the current user to the front of the array
  private _moveCurrentUserToFront(array: any[], currentUserId: number): void {
    const currentIndex = array.findIndex(member => member.userId === currentUserId);
    if (currentIndex > 0) {
      array.unshift(array.splice(currentIndex, 1)[0]);
    }
  }

  public async addComment(item: GroupBasketItemUiData) {
    const me = this;
    if (item.comment !== item.article.Comment) {
      item.article.Comment = item.comment;
    }
    await me._remoteGroupOrderService.addComment(item.article);
  }

  public async addCountItem(item: Article): Promise<void> {
    const me = this;
    // todo besser ein add und remove zu implementieren, gruppen id und user it müssen nicht übergeben werden
    item.Count = item.Count + 1;
    await me._remoteGroupOrderService.PutArticle(me._remoteGroupOrderService.groupId, me._remoteGroupOrderService.userId, item);
  }

  public async removeCountItem(item: Article): Promise<void> {
    const me = this;
    // todo besser ein add und remove zu implementieren, gruppen id und user it müssen nicht übergeben werden
    if (item.Count - 1 == 0) {
      // Count muss dem entsprechen der in der gruppe ist
      await me._remoteGroupOrderService.DeleteArticle(me._remoteGroupOrderService.groupId, me._remoteGroupOrderService.userId, item);
    } else {
      item.Count = item.Count - 1;
      await me._remoteGroupOrderService.PutArticle(me._remoteGroupOrderService.groupId, me._remoteGroupOrderService.userId, item);
    }
  }

  /** change state */
  public async changeStateReady(userId: number, state: boolean) {
    const me = this;
    await me._remoteGroupOrderService.changeAndSetUserStateReady(userId, state);
  }

  public async validateEventAndOpenDialog() {
    const me = this;
    await me._webShopService.handleEventPE();
    let hasPeChanged: boolean = false;
    me._webShopService.peChanged$.pipe(take(1)).subscribe(value => hasPeChanged = value).unsubscribe();
    if (hasPeChanged) {
      const showChangeDialog = me._webShopService.recalcServicePeInBasket();
      if (showChangeDialog) {
        me._eventManagerService.OpenPriceChangeDialog(true);
      }
    }
  }

}